import React from 'react';
import {
    Box,
    Typography,
    List,
    ListItem,
    ListItemAvatar,
    ListItemText,
    ListItemButton,
    Avatar,
    Checkbox,
    Chip,
    Divider
} from '@mui/material';
import { CheckCircle as CheckIcon } from '@mui/icons-material';
import { Moment } from 'moment';

interface Employee {
    name: string;
    availability: Record<string, any>;
    positionAssignments: any[];
    id: string;
    employeeShifts: Record<string, any>[];
}

interface EmployeeSelectionListProps {
    availableEmployees: Employee[];
    partiallyAvailableEmployees: Employee[];
    unavailableEmployees: Employee[];
    selectedEmployees: string[];
    assignmentType: 'day' | 'shift';
    standardEmployees: string[];
    onToggleEmployee: (employeeId: string) => void;
    selectedDay: Moment | undefined;
}

export default function EmployeeSelectionList({
    availableEmployees,
    partiallyAvailableEmployees,
    unavailableEmployees,
    selectedEmployees,
    assignmentType,
    standardEmployees,
    onToggleEmployee,
    selectedDay
}: EmployeeSelectionListProps) {
    const renderEmployeeItem = (employee: Employee, index: number, listLength: number) => (
        <React.Fragment key={employee.id}>
            <ListItem
                disablePadding
                secondaryAction={
                    <Checkbox
                        edge="end"
                        checked={assignmentType === "day" ? selectedEmployees.includes(employee.id) : standardEmployees.includes(employee.id)}
                        onChange={() => onToggleEmployee(employee.id)}
                        disabled={false}
                    />
                }
            >
                <ListItemButton
                    onClick={() => onToggleEmployee(employee.id)}
                    disabled={false}
                    sx={{ py: 1.5 }}
                >
                    <ListItemAvatar>
                        <Avatar sx={{ bgcolor: 'primary.light', width: 36, height: 36 }}>
                            {employee.name.split(' ').map((n: string) => n[0]).join('')}
                        </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                        primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography variant="body2" fontWeight={600}>
                                    {employee.name}
                                </Typography>
                                {selectedEmployees.includes(employee.id) && (
                                    <CheckIcon fontSize="small" color="success" />
                                )}
                            </Box>
                        }
                        secondary={
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mt: 0.5 }}>
                                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                    <Typography variant="caption" fontWeight={500}>
                                        Positions -
                                    </Typography>
                                    {employee.positionAssignments?.map((positionAssignment: any) => (
                                        <Chip
                                            key={positionAssignment.position}
                                            label={positionAssignment.positionName}
                                            size="small"
                                            sx={{ height: 20, fontSize: '0.7rem' }}
                                        />
                                    ))}
                                </Box>
                                {employee.employeeShifts && employee.employeeShifts.length > 0 && (
                                    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                                        <Typography variant="caption" fontWeight={500}>
                                            Assignments -
                                        </Typography>
                                        {employee.employeeShifts.map((shift: any, idx: number) => {
                                            if ((shift.isRecurring && shift.daysOfWeek.includes(selectedDay?.format('dddd').toLowerCase() || '')) ||
                                                (!shift.isRecurring && !shift.cancelShiftId && shift.shiftDate === selectedDay?.format('YYYY-MM-DD'))) {
                                                return <Chip
                                                    key={shift.shiftId || idx}
                                                    label={shift.shiftName}
                                                    size="small"
                                                    color="primary"
                                                    variant="outlined"
                                                    sx={{ height: 20, fontSize: '0.7rem' }}
                                                />
                                            }
                                        })}
                                    </Box>
                                )}
                            </Box>
                        }
                    />
                </ListItemButton>
            </ListItem>
            {listLength - 1 !== index && <Divider />}
        </React.Fragment>
    );

    return (
        <List
            sx={{
                bgcolor: 'grey.50',
                borderRadius: 1,
                maxHeight: 450,
                overflow: 'auto',
                border: 1,
                borderColor: 'divider'
            }}
        >
            <>
                {/* Available Staff Section */}
                <Typography variant="subtitle1" color="primary.main" sx={{ px: 2, pt: 2, pb: 1, display: 'block' }}>
                    Available Staff
                </Typography>
                <Divider variant="middle" />
                {availableEmployees.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No fully available employees found
                        </Typography>
                    </Box>
                ) : (
                    availableEmployees.map((employee, index) => 
                        renderEmployeeItem(employee, index, availableEmployees.length)
                    )
                )}

                {/* Partially Available Staff Section */}
                <Divider />
                <Typography variant="subtitle1" color="info.main" sx={{ px: 2, pt: 2, pb: 1, display: 'block' }}>
                    Partially Available Staff
                </Typography>
                <Divider variant="middle" />
                {partiallyAvailableEmployees.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No partially available employees found
                        </Typography>
                    </Box>
                ) : (
                    partiallyAvailableEmployees.map((employee, index) => 
                        renderEmployeeItem(employee, index, partiallyAvailableEmployees.length)
                    )
                )}

                {/* Unavailable/Unsuitable Staff Section */}
                <Divider />
                <Typography variant="subtitle1" color="error.main" sx={{ px: 2, pt: 2, pb: 1, display: 'block' }}>
                    Unavailable/Unsuitable Staff
                </Typography>
                <Divider variant="middle" />
                {unavailableEmployees.length === 0 ? (
                    <Box sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="body2" color="text.secondary">
                            No unavailable employees found
                        </Typography>
                    </Box>
                ) : (
                    unavailableEmployees.map((employee, index) => 
                        renderEmployeeItem(employee, index, unavailableEmployees.length)
                    )
                )}
            </>
        </List>
    );
}
